/**
 * File Service
 *
 * Purpose: Handles all API communications for file upload and management
 *
 * Architecture: Service layer for file repository operations with upload progress tracking.
 *               Routes file operations through model services (proxy pattern) for security.
 *
 * Proxy Pattern:
 *   - File operations are routed through the model service that owns the entity
 *   - Model service validates entity access before proxying to Core API
 *   - Example: MediaPlan files go through /planning-api/v1/mediaplans/{entity_id}/files/...
 *
 * Dependencies: ApiService, File types
 * Author: Development Team
 * Created: 2025-01-27
 */

import { AxiosRequestConfig } from 'axios';
import { apiService } from './api';
import {
  FileMetadata,
  FileUploadResponse,
  FileListResponse,
  FileDeleteResponse,
  FileBulkDeleteResponse,
  FileListParams,
  FileFieldConfig,
} from '../types/file';

/**
 * Model service configuration for file proxy routing
 *
 * Purpose: Map model_id to the service URL that handles that model
 * Note: Add new model services here as they are implemented
 */
interface ModelServiceConfig {
  baseUrl: string;
  entityPathSegment: string;
}

const MODEL_SERVICE_REGISTRY: Record<string, ModelServiceConfig> = {
  mediaplan: {
    baseUrl: '/planning-api/v1',
    entityPathSegment: 'mediaplans',
  },
  // Add other models as they are implemented:
  // campaign: {
  //   baseUrl: '/planning-api/v1',
  //   entityPathSegment: 'campaigns',
  // },
};

/**
 * Get the proxy URL for a model's file operations
 *
 * Purpose: Determine the correct service URL for file operations
 * Logic: Looks up model in registry, constructs entity-specific URL
 *
 * @param modelId - Model identifier
 * @param entityId - Entity UUID
 * @param endpoint - File endpoint (upload, download, etc.)
 * @returns Full URL for the file operation
 * @throws Error if model is not in registry
 */
function getFileProxyUrl(modelId: string, entityId: string, endpoint: string = ''): string {
  const config = MODEL_SERVICE_REGISTRY[modelId];

  if (!config) {
    throw new Error(
      `Model '${modelId}' is not configured for file operations. ` +
        `Available models: ${Object.keys(MODEL_SERVICE_REGISTRY).join(', ')}`
    );
  }

  // Build URL: /planning-api/v1/mediaplans/{entity_id}/files/{endpoint}
  let url = `${config.baseUrl}/${config.entityPathSegment}/${entityId}/files`;
  if (endpoint) {
    url += `/${endpoint}`;
  }

  return url;
}

class FileService {
  /**
   * Upload a file
   *
   * Purpose: Upload file to the repository with progress tracking
   * Logic: Routes through model service for entity access validation
   *
   * @param file - File to upload
   * @param params - Upload parameters
   * @param onProgress - Optional progress callback
   * @returns Upload response with file metadata
   */
  async upload(
    file: File,
    params: {
      tenant_id: string;
      model_id: string;
      entity_id: string;
      fielddef_id: string;
      description?: string;
      file_config?: FileFieldConfig;
    },
    onProgress?: (progress: number) => void
  ): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tenant_id', params.tenant_id);
    formData.append('fielddef_id', params.fielddef_id);

    if (params.description) {
      formData.append('description', params.description);
    }

    if (params.file_config) {
      formData.append('file_config', JSON.stringify(params.file_config));
    }

    try {
      const url = getFileProxyUrl(params.model_id, params.entity_id, 'upload');

      const response = await apiService.uploadFile<FileUploadResponse>(
        url,
        formData,
        onProgress,
        '' // bypass /core-api prefix — planning-api proxy handles routing
      );

      return response;
    } catch (error: any) {
      console.error('File upload failed:', error);
      return {
        success: false,
        message: error.response?.data?.detail || error.message || 'Upload failed',
        error_code: 'UPLOAD_ERROR',
      };
    }
  }

  /**
   * Get file metadata
   *
   * Purpose: Retrieve file information without downloading content
   * Logic: Routes through model service with entity context
   *
   * @param fileId - File UUID
   * @param params - Context parameters
   * @returns File metadata
   */
  async getMetadata(
    fileId: string,
    params?: {
      model_id: string;
      entity_id: string;
      tenant_id: string;
    }
  ): Promise<FileMetadata> {
    if (params) {
      const baseUrl = getFileProxyUrl(params.model_id, params.entity_id);
      const response = await apiService.get<FileMetadata>(
        `${baseUrl}/${fileId}?tenant_id=${encodeURIComponent(params.tenant_id)}`,
        { baseURL: '' }
      );
      return response;
    }

    // Fallback: Direct Core API call (for backwards compatibility)
    const response = await apiService.get<FileMetadata>(`/v1/files/${fileId}`);
    return response;
  }

  /**
   * Download file
   *
   * Purpose: Download file content and trigger browser download
   * Logic: Routes through model service for access validation
   *
   * @param fileId - File UUID
   * @param filename - Optional filename for download
   * @param params - Context parameters for model service routing
   */
  async download(
    fileId: string,
    filename?: string,
    params?: {
      model_id: string;
      entity_id: string;
      tenant_id: string;
    }
  ): Promise<void> {
    try {
      let url: string;

      let blobConfig: AxiosRequestConfig | undefined;

      if (params) {
        const baseUrl = getFileProxyUrl(params.model_id, params.entity_id);
        url = `${baseUrl}/${fileId}/download?tenant_id=${encodeURIComponent(params.tenant_id)}`;
        blobConfig = { baseURL: '' };
      } else {
        // Fallback: Direct Core API call
        url = `/v1/files/${fileId}/download`;
      }

      const response = await apiService.getBlob(url, blobConfig);

      // Create download link
      const blobUrl = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('File download failed:', error);
      throw error;
    }
  }

  /**
   * Get thumbnail URL
   *
   * Purpose: Get URL for file thumbnail (for images/PDFs/videos)
   * Logic: Routes through model service for access validation
   *
   * @param fileId - File UUID
   * @param params - Context parameters for model service routing
   * @returns Blob URL for thumbnail
   */
  async getThumbnailUrl(
    fileId: string,
    params?: {
      model_id: string;
      entity_id: string;
      tenant_id: string;
    }
  ): Promise<string> {
    try {
      let url: string;

      let blobConfig: AxiosRequestConfig | undefined;

      if (params) {
        const baseUrl = getFileProxyUrl(params.model_id, params.entity_id);
        url = `${baseUrl}/${fileId}/thumbnail?tenant_id=${encodeURIComponent(params.tenant_id)}`;
        blobConfig = { baseURL: '' };
      } else {
        // Fallback: Direct Core API call
        url = `/v1/files/${fileId}/thumbnail`;
      }

      const response = await apiService.getBlob(url, blobConfig);
      return window.URL.createObjectURL(response);
    } catch (error) {
      console.error('Thumbnail fetch failed:', error);
      throw error;
    }
  }

  /**
   * List files
   *
   * Purpose: Get list of files matching criteria
   * Logic: Routes through model service for entity-scoped listing
   *
   * @param params - Filter parameters
   * @returns File list response
   */
  async list(params: FileListParams): Promise<FileListResponse> {
    if (params.model_id && params.entity_id) {
      // Route through model service
      const baseUrl = getFileProxyUrl(params.model_id, params.entity_id);

      const queryParams = new URLSearchParams();
      queryParams.append('tenant_id', params.tenant_id);

      if (params.fielddef_id) {
        queryParams.append('fielddef_id', params.fielddef_id);
      }
      if (params.include_deleted) {
        queryParams.append('include_deleted', 'true');
      }

      const response = await apiService.get<FileListResponse>(
        `${baseUrl}?${queryParams.toString()}`,
        { baseURL: '' }
      );
      return response;
    }

    // Fallback: Direct Core API call (for admin/listing all files)
    const queryParams = new URLSearchParams();
    queryParams.append('tenant_id', params.tenant_id);

    if (params.model_id) {
      queryParams.append('model_id', params.model_id);
    }
    if (params.entity_id) {
      queryParams.append('entity_id', params.entity_id);
    }
    if (params.fielddef_id) {
      queryParams.append('fielddef_id', params.fielddef_id);
    }
    if (params.include_deleted) {
      queryParams.append('include_deleted', 'true');
    }

    const response = await apiService.get<FileListResponse>(`/v1/files?${queryParams.toString()}`);
    return response;
  }

  /**
   * Update file metadata
   *
   * Purpose: Update file description
   * Logic: Routes through model service for access validation
   *
   * @param fileId - File UUID
   * @param data - Update data
   * @param params - Context parameters for model service routing
   * @returns Updated file metadata
   */
  async updateMetadata(
    fileId: string,
    data: { description?: string },
    params?: {
      model_id: string;
      entity_id: string;
      tenant_id: string;
    }
  ): Promise<FileMetadata> {
    if (params) {
      const baseUrl = getFileProxyUrl(params.model_id, params.entity_id);
      const queryParams = new URLSearchParams();
      queryParams.append('tenant_id', params.tenant_id);
      if (data.description !== undefined) {
        queryParams.append('description', data.description);
      }

      const response = await apiService.patch<FileMetadata>(
        `${baseUrl}/${fileId}?${queryParams.toString()}`,
        {},
        { baseURL: '' }
      );
      return response;
    }

    // Fallback: Direct Core API call
    const response = await apiService.patch<FileMetadata>(`/v1/files/${fileId}`, data);
    return response;
  }

  /**
   * Delete file
   *
   * Purpose: Soft delete a file
   * Logic: Routes through model service for access validation
   *
   * @param fileId - File UUID
   * @param params - Context parameters for model service routing
   * @returns Delete response
   */
  async delete(
    fileId: string,
    params?: {
      model_id: string;
      entity_id: string;
      tenant_id: string;
      fielddef_id: string;
    }
  ): Promise<FileDeleteResponse> {
    if (params) {
      const baseUrl = getFileProxyUrl(params.model_id, params.entity_id);
      const queryParams = new URLSearchParams();
      queryParams.append('tenant_id', params.tenant_id);
      queryParams.append('fielddef_id', params.fielddef_id);

      const response = await apiService.delete<FileDeleteResponse>(
        `${baseUrl}/${fileId}?${queryParams.toString()}`,
        { baseURL: '' }
      );
      return response;
    }

    // Fallback: Direct Core API call
    const response = await apiService.delete<FileDeleteResponse>(`/v1/files/${fileId}`);
    return response;
  }

  /**
   * Bulk delete files
   *
   * Purpose: Delete multiple files at once
   * Note: This still goes through Core API as it spans multiple entities
   *
   * @param fileIds - Array of file UUIDs
   * @returns Bulk delete response
   */
  async bulkDelete(fileIds: string[]): Promise<FileBulkDeleteResponse> {
    const response = await apiService.post<FileBulkDeleteResponse>('/v1/files/bulk-delete', {
      file_ids: fileIds,
    });
    return response;
  }

  /**
   * Get file count
   *
   * Purpose: Get count of files for a specific field (for max_files validation)
   * Note: Goes through Core API as it's for validation purposes
   *
   * @param params - Count parameters
   * @returns File count
   */
  async getFileCount(params: {
    tenant_id: string;
    model_id: string;
    entity_id: string;
    fielddef_id: string;
  }): Promise<number> {
    const response = await apiService.get<{ count: number }>(
      `/v1/files/count/${params.tenant_id}/${params.model_id}/${params.entity_id}/${params.fielddef_id}`
    );
    return response.count;
  }

  /**
   * Validate file before upload
   *
   * Purpose: Client-side validation against file_config constraints
   *
   * @param file - File to validate
   * @param config - File field configuration
   * @returns Validation result
   */
  validateFile(file: File, config: FileFieldConfig): { valid: boolean; error?: string } {
    // Check file size
    const maxSizeBytes = config.max_size_mb * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return {
        valid: false,
        error: `File size (${this.formatFileSize(file.size)}) exceeds maximum allowed size (${config.max_size_mb} MB)`,
      };
    }

    // Check file extension
    if (config.allowed_extensions.length > 0) {
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      if (!config.allowed_extensions.includes(extension)) {
        return {
          valid: false,
          error: `File type '.${extension}' is not allowed. Allowed types: ${config.allowed_extensions.join(', ')}`,
        };
      }
    }

    // Check MIME type if specified
    if (config.allowed_mime_types && config.allowed_mime_types.length > 0) {
      if (!config.allowed_mime_types.includes(file.type)) {
        return {
          valid: false,
          error: `File MIME type '${file.type}' is not allowed`,
        };
      }
    }

    return { valid: true };
  }

  /**
   * Check if a model is configured for file proxy routing
   *
   * Purpose: Allow UI to determine if file operations are supported
   *
   * @param modelId - Model identifier
   * @returns True if model is configured
   */
  isModelConfigured(modelId: string): boolean {
    return modelId in MODEL_SERVICE_REGISTRY;
  }

  /**
   * Get list of configured models
   *
   * Purpose: Allow UI to show available file-enabled models
   *
   * @returns Array of configured model IDs
   */
  getConfiguredModels(): string[] {
    return Object.keys(MODEL_SERVICE_REGISTRY);
  }

  /**
   * Format file size for display
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  }
}

// Export singleton instance
export const fileService = new FileService();
export default fileService;
